/**
 * PURE. Bit packing (MSB-first), Crockford base32, CRC-10 (poly 0x233, init 0) and base64url for the custom-name
 * suffix (creatures §A12.1–§A12.3).
 */

/** Writes `value` (≤ 32 bits) MSB-first into `bytes` at bit offset `pos`. */
export function writeBits(bytes: Uint8Array, pos: number, bits: number, value: number): void {
  for (let i = bits - 1; i >= 0; i--) {
    const bit = Math.floor(value / 2 ** i) & 1;
    const byte = pos >> 3;
    const shift = 7 - (pos & 7);
    if (bit) bytes[byte] |= 1 << shift; else bytes[byte] &= ~(1 << shift);
    pos++;
  }
}

/** Reads `bits` (≤ 32) MSB-first from `bytes` at bit offset `pos`. */
export function readBits(bytes: Uint8Array, pos: number, bits: number): number {
  let v = 0;
  for (let i = 0; i < bits; i++) {
    const byte = bytes[(pos + i) >> 3];
    const bit = (byte >> (7 - ((pos + i) & 7))) & 1;
    v = v * 2 + bit;
  }
  return v;
}

/** CRC-10 (poly x^10 + x^9 + x^5 + x^4 + x + 1 = 0x233, init 0, no reflection) over the first `nBits` bits. */
export function crc10(bytes: Uint8Array, nBits: number): number {
  let crc = 0;
  for (let i = 0; i < nBits; i++) {
    const bit = (bytes[i >> 3] >> (7 - (i & 7))) & 1;
    const top = (crc >> 9) & 1;
    crc = (crc << 1) & 0x3ff;
    if (top ^ bit) crc ^= 0x233;
  }
  return crc;
}

/** Crockford base32 alphabet (no I, L, O, U). */
export const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const DECODE: Record<string, number> = (() => {
  const m: Record<string, number> = {};
  for (let i = 0; i < CROCKFORD.length; i++) m[CROCKFORD[i]] = i;
  m.O = 0; m.I = 1; m.L = 1;
  return m;
})();

/** Encodes `nBits` bits (padded with zero bits to a multiple of 5) as Crockford base32. */
export function toBase32(bytes: Uint8Array, nBits: number): string {
  const chars = Math.ceil(nBits / 5);
  let out = '';
  for (let c = 0; c < chars; c++) {
    let v = 0;
    for (let b = 0; b < 5; b++) {
      const i = c * 5 + b;
      const bit = i < nBits ? (bytes[i >> 3] >> (7 - (i & 7))) & 1 : 0;
      v = (v << 1) | bit;
    }
    out += CROCKFORD[v];
  }
  return out;
}

/** Decodes Crockford base32 (case-insensitive; O→0, I/L→1; U and other characters rejected) into `nBytes` bytes.
 *  Returns null on a bad character or non-zero pad bits. */
export function fromBase32(s: string, nBits: number, nBytes: number): Uint8Array | null {
  const out = new Uint8Array(nBytes);
  let pos = 0;
  for (const ch of s.toUpperCase()) {
    const v = DECODE[ch];
    if (v === undefined) return null;
    for (let b = 4; b >= 0; b--) {
      const bit = (v >> b) & 1;
      if (pos < nBits) { if (bit) out[pos >> 3] |= 1 << (7 - (pos & 7)); } else if (bit) return null;
      pos++;
    }
  }
  return pos >= nBits ? out : null;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** UTF-8 string → base64url (no padding). Pure (no TextEncoder dependency). */
export function base64urlEncode(str: string): string {
  const bytes: number[] = [];
  for (const ch of str) {
    let cp = ch.codePointAt(0)!;
    if (cp < 0x80) bytes.push(cp);
    else if (cp < 0x800) bytes.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
    else if (cp < 0x10000) bytes.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    else { cp -= 0; bytes.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63)); }
  }
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    const len = Math.min(3, bytes.length - i);
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
    if (len > 1) out += B64[(n >> 6) & 63];
    if (len > 2) out += B64[n & 63];
  }
  return out;
}

/** base64url → UTF-8 string; null on bad input. */
export function base64urlDecode(s: string): string | null {
  const vals: number[] = [];
  for (const ch of s) {
    const v = B64.indexOf(ch);
    if (v < 0) return null;
    vals.push(v);
  }
  const bytes: number[] = [];
  for (let i = 0; i < vals.length; i += 4) {
    const n = (vals[i] << 18) | ((vals[i + 1] ?? 0) << 12) | ((vals[i + 2] ?? 0) << 6) | (vals[i + 3] ?? 0);
    const len = Math.min(4, vals.length - i);
    if (len < 2) return null;
    bytes.push((n >> 16) & 255);
    if (len > 2) bytes.push((n >> 8) & 255);
    if (len > 3) bytes.push(n & 255);
  }
  let out = '';
  for (let i = 0; i < bytes.length;) {
    const b = bytes[i];
    let cp: number; let n: number;
    if (b < 0x80) { cp = b; n = 1; } else if ((b & 0xe0) === 0xc0) { cp = b & 31; n = 2; } else if ((b & 0xf0) === 0xe0) { cp = b & 15; n = 3; } else if ((b & 0xf8) === 0xf0) { cp = b & 7; n = 4; } else return null;
    for (let k = 1; k < n; k++) {
      const c = bytes[i + k];
      if (c === undefined || (c & 0xc0) !== 0x80) return null;
      cp = (cp << 6) | (c & 63);
    }
    out += String.fromCodePoint(cp);
    i += n;
  }
  return out;
}
